import {PaymentInstructionStatus} from '@sollapay/enums';
import {MetricsCard} from '@sollapay/ui/components';
import {formatNIS, parseMoney} from '@sollapay/utils';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {sumActiveRequestedMoney} from '../../utils';

import type {BuyerPurchaseWithDocumentsDTO, PaymentInstructionDTO} from '@sollapay/types';
import type {MetricColumnConfig} from '@sollapay/ui/components';
import type {FC} from 'react';

export interface BuyerMetricsCardProps {
  purchase: BuyerPurchaseWithDocumentsDTO;
  /** Pre-filtered instructions for this purchase only */
  instructions: PaymentInstructionDTO[];
  /** From `getTrustTypeStrategy(trustPurpose).maxCollectionPercentage` — never hardcode this. */
  maxPercentage: number;
  showsCollectionCap: boolean;
}

export const BuyerMetricsCard: FC<BuyerMetricsCardProps> = ({
  purchase,
  instructions,
  maxPercentage,
  showsCollectionCap
}) => {
  const {t} = useTranslation('trustAccounts');

  const columns = useMemo<MetricColumnConfig[]>(() => {
    const purchasePrice = parseMoney(purchase.purchasePriceNis);
    const maxCollectable = purchasePrice.percentOf(maxPercentage);

    const sentAmount = sumActiveRequestedMoney(instructions);

    const completedAmount = instructions
      .filter(
        i =>
          i.status === PaymentInstructionStatus.COMPLETED ||
          i.status === PaymentInstructionStatus.PARTIALLY_COMPLETED
      )
      .reduce((sum, i) => sum.add(parseMoney(i.allocatedAmountNis)), parseMoney('0'));

    const priceNum = purchasePrice.toNumber();
    const buildProgress = (
      amount: typeof sentAmount,
      color: 'indigo' | 'lime',
      remainingKey: 'remainingToRequest' | 'remainingToPay'
    ) => {
      const percent = priceNum > 0 ? (amount.toNumber() / priceNum) * 100 : 0;
      const remaining = maxCollectable.subtract(amount);
      return {
        value: percent,
        color,
        caption: {
          start: (
            <>
              <strong className="font-bold text-fg">{percent.toFixed(1)}%</strong>{' '}
              {t('view.buyers.detail.metrics.percentOfPurchase')}
            </>
          ),
          end:
            remaining.toNumber() > 0
              ? t(`view.buyers.detail.metrics.${remainingKey}`, {amount: formatNIS(remaining)})
              : undefined
        }
      };
    };

    return [
      {
        label: t('view.buyers.detail.metrics.purchasePrice'),
        value: formatNIS(purchasePrice)
      },
      // An uncapped max just restates the purchase price.
      ...(showsCollectionCap
        ? [
            {
              label: t('view.buyers.detail.metrics.maxCollection'),
              sublabel: `${maxPercentage}%`,
              value: formatNIS(maxCollectable)
            }
          ]
        : []),
      {
        label: t('view.buyers.detail.metrics.paymentRequestsSent'),
        value: formatNIS(sentAmount),
        progress: buildProgress(sentAmount, 'indigo', 'remainingToRequest')
      },
      {
        label: t('view.buyers.detail.metrics.completedPayments'),
        value: formatNIS(completedAmount),
        progress: buildProgress(completedAmount, 'lime', 'remainingToPay')
      }
    ];
  }, [purchase.purchasePriceNis, instructions, maxPercentage, showsCollectionCap, t]);

  return <MetricsCard columns={columns} />;
};
